/**
 * One person, with everything dated.
 *
 * The list answers "who does Compass know". This answers "what has happened
 * with them", which is the only version of a person screen worth opening
 * twice. Three streams, one timeline, newest first:
 *
 *   said     what was said ABOUT them — editable, because a derived line is
 *            a guess until the person confirms it ("picking things up fst")
 *   heard    every reference that NAMED them, with its date. NOT editable:
 *            that is the record of what was actually said, and a record you
 *            can rewrite has stopped being one.
 *   map      the items on the map they are involved in
 *
 * And two repairs the derivation cannot make for itself: merging the two rows
 * one person arrives as ("call my daughter" → `daughter`; "my daughter
 * Sahasra is 15" → `Sahasra`), and forgetting someone entirely.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Check, Loader2, MessageCircle, Pencil, Quote,
  Sparkles, Trash2, UserRound, Users, X,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { ConfirmationModal, useConfirmationModal } from '../components/ui/ConfirmationModal'
import { TagEditor } from '../components/people/TagEditor'
import {
  LangPicker, LiveTranscript, MicButton, MicTrouble, useDictationInto,
} from '../components/dictation/Dictation'
import {
  hortonV3Service,
  type CapturedPerson, type PersonCorrection, type PersonNote,
  type PersonPage as PersonPageData,
} from '../services/hortonV3Service'
import { BRAND } from '../config/brand'
import { parseServerUtc } from '../utils/datetime'
import { MediaStrip } from '../components/media/MediaStrip'

// ── dates, the way a person refers to their own days ────────────────────────

/**
 * Ordering and day-grouping both hang off one parse, and it has to be
 * `parseServerUtc`. A naive server stamp ("2026-09-08T03:38:14") has no zone,
 * so `new Date()` reads it as LOCAL — which shifts every row by the offset and,
 * for anything near midnight, files it under the wrong DAY. West of UTC that
 * silently moved "Today" into "Yesterday".
 *
 * An unparseable stamp sorts to the BOTTOM rather than to the epoch: NaN makes
 * a comparator inconsistent and the whole list order arbitrary, which is a far
 * worse failure than one row in the wrong place.
 */
function stampMs(iso?: string | null): number {
  const d = parseServerUtc(iso)
  return d ? d.getTime() : Number.NEGATIVE_INFINITY
}

function dayKey(iso: string): string {
  const d = parseServerUtc(iso)
  if (!d) return 'unknown'
  // Local calendar day, deliberately: a person groups their own days by the
  // clock on their wall, not by UTC.
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function dayLabel(iso: string): string {
  const d = parseServerUtc(iso)
  if (!d) return 'Undated'
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' })
  return d.toLocaleDateString(undefined, {
    month: 'long', day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** Where a line came from, in words rather than a field name. */
function provenance(source: string | null, edited?: boolean): string {
  if (source === 'user') return 'you wrote this'
  if (edited) return source === 'diary' ? 'from your diary · you edited it' : 'you edited it'
  if (source === 'diary') return 'from your diary'
  return 'from a conversation'
}

type Row =
  | { at: string; sort: number; type: 'said'; note: PersonNote }
  | { at: string; sort: number; type: 'heard'; gist: string; sensitive: boolean }

// ── one editable line ───────────────────────────────────────────────────────

function SaidRow({ note, onSaved }: { note: PersonNote; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(note.note)
  // Correcting a line by voice too. Appending, like everywhere else — a
  // correction that wiped what was there before it finalised would be the
  // one place dictation could destroy something.
  const dictation = useDictationInto(setText, 'horton.person.dictationLang')
  const [kind, setKind] = useState<'attribute' | 'impression'>(
    note.kind === 'attribute' ? 'attribute' : 'impression')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!text.trim()) return
    setBusy(true)
    const r = await hortonV3Service.editPersonNote(note.id, text.trim(), kind)
    setBusy(false)
    if (!r.ok) { setError(r.error ?? 'that did not save'); return }
    setEditing(false)
    onSaved()
  }

  const remove = async () => {
    setBusy(true)
    const r = await hortonV3Service.deletePersonNote(note.id)
    setBusy(false)
    if (!r.ok) { setError(r.error ?? 'that did not save'); return }
    onSaved()
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                  className="w-full rounded border border-soft bg-surface p-2 text-sm text-primary
                             focus:outline-none focus:ring-1 focus:ring-brand" />
        <LiveTranscript dictation={dictation} />
        <div className="flex items-center gap-2 flex-wrap">
          {/* The distinction the reply model reads: a standing fact may be
              said back plainly, an impression is attributed to them ("you've
              said…"). Worth exposing, because only they know which it is. */}
          <KindToggle value={kind} onChange={setKind} />
          <Button size="sm" onClick={() => { if (dictation.isListening) dictation.stop(); void save() }}
                  disabled={busy || !text.trim()}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <MicButton dictation={dictation} disabled={busy} />
          <Button size="sm" variant="outline"
                  onClick={() => { dictation.stop(); setEditing(false); setText(note.note) }}>
            <X className="w-3.5 h-3.5" />
          </Button>
          <button onClick={() => void remove()} disabled={busy}
                  className="ml-auto p-1.5 rounded hover:bg-surface-alt text-muted hover:text-bad"
                  title="Remove this line">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {error && <p className="text-xs text-bad">{error}</p>}
      </div>
    )
  }

  return (
    <div className="group flex items-start gap-2">
      <Quote className="w-3.5 h-3.5 text-brand mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-secondary">{note.note}</p>
        <p className="text-xs text-muted mt-0.5">
          {note.kind === 'attribute' ? 'fact' : 'impression'} · {provenance(note.source, note.edited)}
        </p>
      </div>
      <button onClick={() => setEditing(true)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded
                         hover:bg-surface-alt text-muted hover:text-brand transition-opacity"
              title="Edit this line">
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function KindToggle({ value, onChange }: {
  value: 'attribute' | 'impression'
  onChange: (v: 'attribute' | 'impression') => void
}) {
  return (
    <div className="inline-flex rounded border border-soft overflow-hidden text-xs">
      {(['attribute', 'impression'] as const).map((k) => (
        <button key={k} onClick={() => onChange(k)}
                title={k === 'attribute'
                  ? `A standing fact — ${BRAND.assistant} may say it back plainly.`
                  : `How they seemed to you at a moment — ${BRAND.assistant} attributes it to you.`}
                className={`px-2 py-1 ${value === k ? 'bg-brand text-white' : 'text-muted hover:bg-surface-alt'}`}>
          {k === 'attribute' ? 'fact' : 'impression'}
        </button>
      ))}
    </div>
  )
}

// ── the page ────────────────────────────────────────────────────────────────

export default function PersonPage() {
  const { entityId } = useParams<{ entityId: string }>()
  const id = Number(entityId)
  const navigate = useNavigate()
  const modal = useConfirmationModal()

  const [data, setData] = useState<PersonPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [others, setOthers] = useState<CapturedPerson[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  // What the person has already decided about this someone. Shown because a
  // record that quietly disagrees with your corrections is worse than one
  // that never had them: you should be able to see what it was told.
  const [corrections, setCorrections] = useState<PersonCorrection[]>([])

  const [name, setName] = useState('')
  const [relation, setRelation] = useState('')
  const [editingHead, setEditingHead] = useState(false)
  const [savingHead, setSavingHead] = useState(false)

  const [draft, setDraft] = useState('')
  const [draftKind, setDraftKind] = useState<'attribute' | 'impression'>('attribute')
  const [adding, setAdding] = useState(false)
  // Remembering something about someone is the other place people would
  // rather talk than type — usually right after seeing them, on a phone.
  // Its own remembered language: a diary and a note about family are not
  // necessarily written in the same one.
  const dictation = useDictationInto(setDraft, 'horton.person.dictationLang')

  const load = useCallback(async () => {
    setLoading(true)
    const page = await hortonV3Service.getPerson(id)
    if (!page || !page.person) { setError('That person is not here.'); setLoading(false); return }
    setData(page)
    setName(page.person.name)
    setRelation(page.person.relation_to_user ?? '')
    setLoading(false)
  }, [id])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void hortonV3Service.getEnginePeople().then((r) => setOthers(r.people))
    void hortonV3Service.getTags().then((t) => setSuggestions(t.map((x) => x.tag)))
    void hortonV3Service.getPersonCorrections().then(setCorrections)
  }, [])

  const rows = useMemo<Row[]>(() => {
    if (!data) return []
    const out: Row[] = [
      ...(data.said ?? []).map((n) => ({
        at: n.ts, sort: stampMs(n.ts), type: 'said' as const, note: n,
      })),
      ...(data.heard ?? []).map((h) => ({
        at: h.ts, sort: stampMs(h.ts), type: 'heard' as const,
        gist: h.gist, sensitive: h.sensitive,
      })),
    ]
    // Newest first. Ties break on type then on the note id so two rows
    // recorded in the same millisecond keep a fixed order between renders —
    // an unstable comparator makes a list appear to reshuffle on its own.
    return out.sort((a, b) => {
      if (a.sort !== b.sort) return b.sort - a.sort
      if (a.type !== b.type) return a.type === 'said' ? -1 : 1
      const ida = a.type === 'said' ? String(a.note.id) : a.gist
      const idb = b.type === 'said' ? String(b.note.id) : b.gist
      return ida.localeCompare(idb)
    })
  }, [data])

  const days = useMemo(() => {
    const groups: Array<{ key: string; label: string; rows: Row[] }> = []
    for (const r of rows) {
      const key = dayKey(r.at)
      const last = groups[groups.length - 1]
      if (last && last.key === key) last.rows.push(r)
      else groups.push({ key, label: dayLabel(r.at), rows: [r] })
    }
    return groups
  }, [rows])

  const saveHead = async () => {
    if (!name.trim()) return
    setSavingHead(true)
    const r = await hortonV3Service.updatePerson(id, {
      name: name.trim(), relation_to_user: relation.trim() || null,
    })
    setSavingHead(false)
    if (!r.ok) { setError(r.error ?? 'that did not save'); return }
    setEditingHead(false)
    void load()
  }

  const saveTags = async (tags: string[]) => {
    setData((d) => (d ? { ...d, person: { ...d.person, tags } } : d))   // optimistic
    const r = await hortonV3Service.updatePerson(id, { tags })
    if (!r.ok) { setError(r.error ?? 'the tags did not save'); void load() }
    else void hortonV3Service.getTags().then((t) => setSuggestions(t.map((x) => x.tag)))
  }

  const addLine = async () => {
    if (!draft.trim()) return
    // What is still being said belongs to the line being saved, not the next
    // one: close the mic before the write.
    if (dictation.isListening) dictation.stop()
    setAdding(true)
    const r = await hortonV3Service.addPersonNote(id, draft.trim(), draftKind)
    setAdding(false)
    if (!r.ok) { setError(r.error ?? 'that did not save'); return }
    setDraft('')
    void load()
  }

  const merge = async (dropId: number, dropName: string) => {
    const ok = await modal.confirm(
      'Same person?',
      `Everything recorded under "${dropName}" moves onto ${data?.person.name}, and ` +
      `"${dropName}" becomes another name for them. This cannot be undone.`,
      { confirmText: 'Merge', cancelText: 'Cancel' })
    if (!ok) return
    const r = await hortonV3Service.mergePeople(id, dropId)
    if (!r.ok) { setError(r.error ?? 'the merge did not happen'); return }
    setOthers((o) => o.filter((p) => p.id !== dropId))
    void hortonV3Service.getPersonCorrections().then(setCorrections)
    void load()
  }

  const forget = async () => {
    const ok = await modal.confirm(
      `Forget ${data?.person.name}?`,
      'Everything recorded about them goes. What you actually said stays in your ' +
      `history — this removes the person ${BRAND.assistant} built from it.`,
      { confirmText: 'Forget', cancelText: 'Keep' })
    if (!ok) return
    const r = await hortonV3Service.forgetPerson(id)
    if (!r.ok) { setError(r.error ?? 'that did not happen'); return }
    navigate('/my-people')
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 flex items-center gap-2 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    )
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 space-y-4">
        <p className="text-sm text-muted">{error ?? 'That person is not here.'}</p>
        <Link to="/my-people" className="text-sm text-brand">Back to My People</Link>
      </div>
    )
  }

  const p = data.person
  const mergeable = others.filter((o) => o.id !== id)
  const mine = corrections.filter((c) => c.entity_id === id)

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <ConfirmationModal isOpen={modal.isOpen} onClose={modal.close} {...modal.modalProps} />

      <Link to="/my-people" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-brand">
        <ArrowLeft className="w-4 h-4" /> My People
      </Link>

      {/* Who they are — the two fields the engine DERIVED, and the person's to fix */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-info/10 text-brand">
              <UserRound className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              {editingHead ? (
                <div className="space-y-2">
                  <input value={name} onChange={(e) => setName(e.target.value)}
                         placeholder="Their name"
                         className="w-full rounded border border-soft bg-surface px-2 py-1
                                    text-lg text-primary focus:outline-none focus:ring-1 focus:ring-brand" />
                  <input value={relation} onChange={(e) => setRelation(e.target.value)}
                         placeholder="Who they are to you — in your words"
                         className="w-full rounded border border-soft bg-surface px-2 py-1
                                    text-sm text-secondary focus:outline-none focus:ring-1 focus:ring-brand" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void saveHead()} disabled={savingHead || !name.trim()}>
                      {savingHead ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => { setEditingHead(false); setName(p.name); setRelation(p.relation_to_user ?? '') }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold text-primary truncate">{p.name}</h1>
                    <button onClick={() => setEditingHead(true)}
                            className="p-1 rounded hover:bg-surface-alt text-muted hover:text-brand"
                            title="Edit name and relationship">
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-muted">
                    {p.relation_to_user || 'You haven’t said how you know them'}
                    {(p.aliases ?? []).length > 0 && <> · also “{(p.aliases ?? []).join('”, “')}”</>}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Who they are, in a sentence or two, rolled up from everything
              below. The evidence stays underneath: a summary with nothing
              under it is a rumour. */}
          {p.summary && (
            <p className="text-sm text-secondary leading-relaxed">{p.summary}</p>
          )}

          {/* A face, under the name. Images only (owner 2026-09-08). */}
          <MediaStrip
            kind="person"
            ownerId={Number(p.id)}
            label={p.name}
            className="border-t border-soft pt-4"
          />

          <div>
            <p className="text-app-micro font-medium text-secondary mb-1.5">Tags</p>
            <TagEditor value={p.tags ?? []} suggestions={suggestions} onChange={(t) => void saveTags(t)} />
          </div>

          {(data.map ?? []).length > 0 && (
            <div>
              <p className="text-app-micro font-medium text-secondary mb-1.5">On your map</p>
              <div className="flex flex-wrap gap-1.5">
                {(data.map ?? []).map((it) => (
                  <span key={it.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-info/10 text-xs text-secondary">
                    <Sparkles className="w-3 h-3 text-brand" />
                    {it.display_title || it.title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(p.relations ?? []).length > 0 && (
            <div>
              <p className="text-app-micro font-medium text-secondary mb-1.5">Connections</p>
              {(p.relations ?? []).map((r, i) => (
                <p key={i} className="text-sm text-secondary">{r}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {error && <p className="text-sm text-bad">{error}</p>}

      {/* Add a line yourself */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <textarea
            value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void addLine() }
            }}
            rows={2} placeholder={`Something to remember about ${p.name}…`}
            className="w-full rounded border border-soft bg-surface p-2 text-sm text-primary
                       placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-brand" />
          <LiveTranscript dictation={dictation}
                          idle={`Listening — say what you want to remember about ${p.name}.`} />
          <div className="flex items-center gap-2 flex-wrap">
            <KindToggle value={draftKind} onChange={setDraftKind} />
            <Button size="sm" onClick={() => void addLine()} disabled={adding || !draft.trim()}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
            <MicButton dictation={dictation} disabled={adding} />
            <LangPicker dictation={dictation} disabled={adding} />
          </div>
          <MicTrouble dictation={dictation} />
        </CardContent>
      </Card>

      {/* The timeline */}
      <div className="space-y-5">
        {days.length === 0 && (
          <p className="text-sm text-muted">
            Nothing recorded about {p.name} yet. Mention them in a conversation or a
            diary entry, or add a line above.
          </p>
        )}
        {days.map((day) => (
          <section key={day.key}>
            <h2 className="text-app-micro font-semibold text-secondary mb-2 pb-1 border-b border-soft">
              {day.label}
            </h2>
            <div className="space-y-3">
              {day.rows.map((r, i) => r.type === 'said' ? (
                <SaidRow key={`s-${r.note.id}`} note={r.note} onSaved={() => void load()} />
              ) : (
                <div key={`h-${i}`} className="flex items-start gap-2">
                  {/* A reference, not a claim: what was actually said that
                      day. Read-only on purpose. */}
                  <MessageCircle className="w-3.5 h-3.5 text-muted mt-1 flex-shrink-0" />
                  <p className={`text-sm ${r.sensitive ? 'text-muted italic' : 'text-secondary'}`}>
                    {r.gist}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ))}
        {data.said_withheld > 0 && (
          <p className="text-xs text-muted italic">
            {data.said_withheld} more kept private — {BRAND.assistant} will not repeat those back.
          </p>
        )}
      </div>

      {/* What they have already told Compass about this someone. It is shown
          back so the memory is visible rather than implied — and because the
          engine's own reconciliation pass is handed exactly this list and is
          not allowed to undo any of it. */}
      {mine.length > 0 && (
        <div className="pt-4 border-t border-soft">
          <p className="text-app-micro font-medium text-secondary mb-1.5">
            What you’ve told {BRAND.assistant}
          </p>
          <ul className="space-y-1">
            {mine.map((c, i) => (
              <li key={i} className="text-sm text-secondary">
                {c.kind === 'merged' && <>“{c.subject}” is another name for {c.detail}</>}
                {c.kind === 'renamed' && <>you renamed “{c.subject}” to {c.detail}</>}
                {c.kind === 'relation' && <>you set who they are to you: {c.detail}</>}
                {c.kind === 'forgotten' && <>you removed “{c.subject}”</>}
                <span className="text-muted"> · {dayLabel(c.ts).toLowerCase()}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted mt-1.5">
            {BRAND.assistant} keeps these. It won’t undo them, and a name you merged folds
            here automatically from now on.
          </p>
        </div>
      )}

      {/* The two repairs the engine cannot make for itself */}
      <div className="pt-4 border-t border-soft space-y-3">
        {mergeable.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted hover:text-secondary">
              Is {p.name} the same person as someone else here?
            </summary>
            <p className="text-xs text-muted mt-2 mb-2">
              {BRAND.assistant} records a name it hears and a relationship it is told, so one
              person can arrive twice — “my daughter” once, “Sahasra” another time.
              Pick the duplicate and everything moves onto {p.name}.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {mergeable.map((o) => (
                <button key={o.id} onClick={() => void merge(o.id, o.name)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full
                                   border border-soft text-xs text-secondary hover:bg-surface-alt">
                  <Users className="w-3 h-3" /> {o.name}
                  {o.relation_to_user ? <span className="text-muted">· {o.relation_to_user}</span> : null}
                </button>
              ))}
            </div>
          </details>
        )}
        <button onClick={() => void forget()}
                className="text-xs text-muted hover:text-bad inline-flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Forget {p.name}
        </button>
      </div>
    </div>
  )
}

